import React, { useCallback, useEffect, useRef } from 'react';
import { clsx } from 'clsx';

import ActionsCancelNormalXsmall from '@strava/icons/ActionsCancelNormalXsmall';
import { CameraStateFlags } from '@strava/mre';
import { LatLng } from '@strava/ui/Map';

import { useMRE } from '../../hooks/useMRE';
import { Paragraph } from './Paragraph/Paragraph';
import { Title } from './Title/Title';
import { getPopupAnchorClassName, getPopupAnchorPosition } from './Popup.utils';

import styles from './Popup.module.scss';

// compound-component export
interface PopupComponent extends React.FC<PopupProps> {
  Title: typeof Title;
  Paragraph: typeof Paragraph;
}

export interface PopupProps {
  latLng: LatLng;
  children: React.ReactNode;
  className?: string;
  isDismissible?: boolean;
}

// How much space to leave between the marker and the popup, to accommodate both the callout triangle marker,
// and any empty space which we want to include.
const CALLOUT_SPACE_PX = 15;

export const Popup: PopupComponent = ({
  latLng,
  children,
  className,
  isDismissible
}: PopupProps) => {
  const { mapContainerRef, terrainEngine } = useMRE();

  // We set popup position by using a ref and applying style modifications rather than re-rendering the popup in a new position.
  const mrePopupRef = useRef<HTMLDivElement>(null);

  const updateLocation = useCallback(() => {
    if (!mrePopupRef.current) {
      return;
    }

    if (!terrainEngine || !mapContainerRef.current || !latLng) {
      mrePopupRef.current.style.display = 'none';
      return;
    }

    // Get the screen position of the latLng in the terrain engine.
    const screenPos = terrainEngine
      .getCamera()
      .getScreenPosition({ latitude: latLng[0], longitude: latLng[1] });

    if (!screenPos || screenPos.isOccluded) {
      mrePopupRef.current.style.display = 'none';
      return;
    }

    // Get the horizontal and vertical anchor position for the popup
    // Defined by an (x,y) grid with values from -1 to 1, with the content screen position at he center (0,0).
    const { horizontal, vertical } = getPopupAnchorPosition(
      screenPos.x * mapContainerRef.current.clientWidth, // distance across from the left
      screenPos.y * mapContainerRef.current.clientHeight, // distance down from the top
      mapContainerRef.current.clientWidth,
      mapContainerRef.current.clientHeight,
      mrePopupRef.current.clientWidth,
      mrePopupRef.current.clientHeight,
      CALLOUT_SPACE_PX
    );

    // Percentage translations account for the main positioning of the popover.
    const translateXPc = -50 * (1 + horizontal);
    const translateYPc = -50 * (1 + vertical); // 1 means anchor below, and we need to lift the popup up

    // Pixel translations are just to accommodate the extra offset from callout to marker.
    // x-offset is zero unless we are in one of the side anchor positions.
    const translateXPx =
      vertical === 0 ? CALLOUT_SPACE_PX * -1 * horizontal : 0;
    const translateYPx = CALLOUT_SPACE_PX * -1 * vertical;

    // Show the popup
    mrePopupRef.current.style.display = 'initial';

    // Transform is more performant than top/left etc; it does not require recalculating position of other elements.
    mrePopupRef.current.style.transform = `translateX(${translateXPc}%) translateY(${translateYPc}%) translateX(${translateXPx}px) translateY(${translateYPx}px) translate(${screenPos.x * mapContainerRef.current.clientWidth}px, ${screenPos.y * mapContainerRef.current.clientHeight}px)`;

    // The rest of the work here is for the little callout thingy!
    const classNames = mrePopupRef.current.className.split(' ');

    const newClassName = getPopupAnchorClassName(horizontal, vertical);

    const oldClassName = classNames.length
      ? classNames[classNames.length - 1]
      : undefined;
    if (oldClassName !== newClassName) {
      classNames.pop();
      classNames.push(newClassName);
      mrePopupRef.current.className = classNames.join(' ');
    }
  }, [latLng, terrainEngine, mapContainerRef]); // ref added to deps array to keep linter happy - has no functional impact

  // when the latLng changes then we trigger an immediate location update
  useEffect(() => {
    if (!latLng || !mapContainerRef.current || !terrainEngine) {
      return;
    }

    updateLocation();
  }, [latLng, mapContainerRef, terrainEngine, updateLocation]);

  // bind updateLocation to camera target changes
  useEffect(() => {
    if (!terrainEngine) {
      return;
    }

    const cameraStateListener = {
      stateToObserve: () => CameraStateFlags.TARGET,
      onViewTypeChanged: () => {},
      onTargetChanged: () => {
        updateLocation();
      }
    };

    terrainEngine.getCamera().addStateListener(cameraStateListener);

    return () => {
      terrainEngine.getCamera().removeStateListener(cameraStateListener);
    };
  }, [terrainEngine, updateLocation]);

  const handleDismiss = () => {
    if (mrePopupRef.current) {
      mrePopupRef.current.style.display = 'none';
    }
  };

  return (
    <div
      // We later rely on the last class name in the list being the popup anchor position to simplify the class name manipulation.
      className={clsx(styles.popup, className, styles.popupAnchorBottom)}
      ref={mrePopupRef}
      data-testid="mre-popup"
    >
      {isDismissible && (
        <button onClick={handleDismiss} className={styles.closeButton}>
          <ActionsCancelNormalXsmall />
        </button>
      )}
      {children}
      <div className={styles.callout} />
    </div>
  );
};

Popup.Title = Title;
Popup.Paragraph = Paragraph;
